'use client';

import type { CommunityComponent, TransformedCommunityData } from '@/types/community';
import CommonSpacer from '@/components/sections/shared/CommonSpacer';
import CommunityHero from '../components/CommunityHero';
import CommunityLeaderQuoteSection from '../components/CommunityLeaderQuote';
import CommunityNavCards from '../components/CommunityNavCards';
import CommunityStatsSection from '../components/CommunityStatsSection';
import CommunityZigzagSectionView from '../components/CommunityZigzagSection';
import CommunityNeevSectionView from '../components/CommunityNeevSection';
import CommunityPreFooterSection from '../components/CommunityPreFooter';

interface CommunityClientProps {
  locale: string;
  data: TransformedCommunityData;
}

function renderCommunityComponent(
  component: CommunityComponent,
  locale: string,
  zigzagIndex: number,
) {
  switch (component.__component) {
    case 'common.spacer-top':
    case 'common.spacer-full':
    case 'common.spacer-bottom':
      return <CommonSpacer key={component.id} zone={component} />;

    case 'community.leader-quote':
      return <CommunityLeaderQuoteSection key={component.id} zone={component} />;

    case 'community.nav-cards':
      return <CommunityNavCards key={component.id} locale={locale} zone={component} />;

    case 'sustainability-inner.stats-section':
      return <CommunityStatsSection key={component.id} zone={component} />;

    case 'products.zigzag-section':
      return (
        <CommunityZigzagSectionView
          key={component.id}
          zone={component}
          isEven={zigzagIndex % 2 === 0}
        />
      );

    case 'about-us.neev-section':
      return <CommunityNeevSectionView key={component.id} zone={component} />;

    case 'community.community-prefooter':
      return <CommunityPreFooterSection key={component.id} locale={locale} zone={component} />;

    default:
      return null;
  }
}

export default function CommunityClient({ locale, data }: CommunityClientProps) {
  const hasBannerContent =
    data.bannerHeading.length > 0 ||
    Boolean(data.bannerDescription?.trim()) ||
    Boolean(data.bannerMedia);

  let zigzagIndex = 0;

  return (
    <div className="min-h-screen bg-[#f0ece8] font-figtree text-[#1a1a1a] antialiased">
      {hasBannerContent ? (
        <CommunityHero
          bannerHeading={data.bannerHeading}
          bannerDescription={data.bannerDescription}
          bannerMedia={data.bannerMedia}
        />
      ) : null}

      {data.components.map((component) => {
        if (component.__component === 'products.zigzag-section') {
          const node = renderCommunityComponent(component, locale, zigzagIndex);
          zigzagIndex += 1;
          return node;
        }
        return renderCommunityComponent(component, locale, zigzagIndex);
      })}
    </div>
  );
}
